import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import {
  Box,
  Button,
  CircularProgress,
  Tab,
} from '@mui/material';
import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { COLD_HOURS_NAME } from '../../../../constants/graphs';
import useChartConfig from '../../../../hooks/useChartConfig';
import { coldHoursConfig } from '../../../../requests/api/apiClient';
import ConversationsTab from '../../ConversationTab';
import useStyles from '../../styles';
import DayMonthBaseForm, { ConfigState } from '../../utils/DayMonthBaseForm';
import { FORM_CONFIGS } from './constants';
import { ColdHoursConfigType, DEFAULT_COLD_HOURS_CONFIG } from './types';
import { validateConfig } from './validations';
import { ModalState, EMPTY_MODAL_STATE } from '../../chartModal';
import useIsClientOperator from '../../../../hooks/useIsClientOperator';

const transformBackendData = (
  backendConfig: Record<string, number>,
  defaultConfig: ColdHoursConfigType,
): ColdHoursConfigType => ({
  coldHoursBaseTemp: backendConfig.coldHoursBaseTemp
    ?? defaultConfig.coldHoursBaseTemp,
  coldHoursStartMonth: backendConfig.coldHoursStartMonth
    ?? defaultConfig.coldHoursStartMonth,
  coldHoursStartDay: backendConfig.coldHoursStartDay
    ?? defaultConfig.coldHoursStartDay,
  coldHoursEndMonth: backendConfig.coldHoursEndMonth
    ?? defaultConfig.coldHoursEndMonth,
  coldHoursEndDay: backendConfig.coldHoursEndDay
    ?? defaultConfig.coldHoursEndDay,
  accumulatedDegreesBaseTemp: backendConfig.accumulatedDegreesBaseTemp
    ?? defaultConfig.accumulatedDegreesBaseTemp,
  accumulatedDegreesStartMonth: backendConfig.accumulatedDegreesStartMonth
    ?? defaultConfig.accumulatedDegreesStartMonth,
  accumulatedDegreesStartDay: backendConfig.accumulatedDegreesStartDay
    ?? defaultConfig.accumulatedDegreesStartDay,
  accumulatedDegreesEndMonth: backendConfig.accumulatedDegreesEndMonth
    ?? defaultConfig.accumulatedDegreesEndMonth,
  accumulatedDegreesEndDay: backendConfig.accumulatedDegreesEndDay
    ?? defaultConfig.accumulatedDegreesEndDay,
});

const ColdHoursConfigContent = ({
  sectorId,
  setModalState: setModalStateProp,
  refreshCounter = 0,
  onConfigSaved,
}: {
  sectorId: string;
  setModalState?: React.Dispatch<React.SetStateAction<ModalState>>;
  refreshCounter?: number;
  onConfigSaved?: () => void;
}) => {
  const classes = useStyles();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);

  // Default to the conversations tab when arriving from a notification (date param).
  const date = queryParams.get('date');
  const initialTabValue = date ? '2' : '1';

  const [tabValue, setTabValue] = useState(initialTabValue);

  const [, setInternalModalState] = useState<ModalState>(EMPTY_MODAL_STATE);
  const setModalState = setModalStateProp ?? setInternalModalState;

  const {
    formConfig,
    loading,
    errors,
    handleConfigChange,
    handleSave,
  } = useChartConfig<ColdHoursConfigType>({
    sectorId,
    apiService: coldHoursConfig,
    defaultConfig: DEFAULT_COLD_HOURS_CONFIG,
    configKey: 'coldHoursConfig',
    validateConfig,
    transformBackendData,
    transformPayload: (cfg) => ({
      ...cfg,
      coldHoursBaseTemp: Number(cfg.coldHoursBaseTemp),
      accumulatedDegreesBaseTemp: Number(cfg.accumulatedDegreesBaseTemp),
    }),
    messages: {
      loadError: 'No se pudo cargar la configuración de horas de frío',
      saveSuccess: 'Configuración guardada correctamente',
      saveError: 'Error al guardar la configuración',
      validationError: 'Por favor, resolver errores en el formulario',
    },
  });

  const isClientOperator = useIsClientOperator();

  useEffect(() => {
    if (isClientOperator && tabValue === '1') {
      setTabValue('2');
    }
  }, [isClientOperator, tabValue]);

  const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  }, []);

  const onSave = useCallback(async () => {
    const saved = await handleSave();
    // onConfigSaved refreshes chart data — only fire when the save actually succeeded.
    if (saved) {
      onConfigSaved?.();
    }
  }, [handleSave, onConfigSaved]);

  const renderForms = useCallback(() => (
    FORM_CONFIGS.map((formConfigItem) => (
      <DayMonthBaseForm
        key={formConfigItem.title}
        {...formConfigItem}
        config={formConfig}
        onConfigChange={handleConfigChange as (
          field: keyof ConfigState, value: string | number) => void}
        errors={errors}
      />
    ))
  ), [formConfig, errors, handleConfigChange]);

  return (
    <TabContext value={tabValue}>
      <Box sx={{ borderBottom: 0 }}>
        <TabList onChange={handleTabChange} aria-label="sector tabs">
          {!isClientOperator && (
            <Tab label="Configuración" value="1" className={classes.tabLabel} />
          )}
          <Tab label="Conversaciones" value="2" className={classes.tabLabel} />
        </TabList>
      </Box>
      {!isClientOperator && (
        <TabPanel value="1" className={classes.configTabPanel}>
          {renderForms()}
          <Box mt={3} display="flex" justifyContent="flex-end">
            {loading ? <CircularProgress /> : (
              <Button
                variant="contained"
                color="primary"
                onClick={onSave}
                disabled={loading || Object.keys(errors).length > 0}
              >
                Guardar
              </Button>
            )}
          </Box>
        </TabPanel>
      )}
      <TabPanel value="2" className={classes.configTabPanel}>
        <ConversationsTab
          setModalState={setModalState}
          sectorId={sectorId}
          chartName={COLD_HOURS_NAME}
          refreshCounter={refreshCounter}
        />
      </TabPanel>
    </TabContext>
  );
};

export default ColdHoursConfigContent;
